/**
 * SMS alerts service — migrated to typed `@tiaki/http-client` per ADR 113
 * §Decision (call-site path autocomplete).
 *
 * Note: SMS paths now use `/api/v2/...` prefix to match the OpenAPI schema.
 * (The legacy `apiClient` adapter prepended `/api/v2/` automatically; the
 * typed `client.GET/POST/...` requires the full path.)
 */
import { client } from '../lib/http-client';

function unwrap<T>(result: { data?: T; error?: unknown }): T {
  if (result.error !== undefined) {
    throw result.error;
  }
  return result.data as T;
}

export interface AlertHistory {
  id: number;
  name: number | null;
  created: string;
  cancelled: string | null;
  initiator: number | null;
  canceller: number | null;
  last_police_status: string;
  scoped: boolean;
  silent: boolean;
  internal: boolean;
  pending_approval: boolean;
  school: number;
  alert_type_name: string | null;
  alert_type_icon: string | null;
  site_name: string;
  initiator_first_name: string | null;
  canceller_first_name: string | null;
  initiator_last_name: string | null;
  canceller_last_name: string | null;
  is_origin: boolean;
  type: 'alert' | 'report';
  original_incident_id: number | null;
  /**
   * Pk of the originating Alert (set at row creation; legacy rows may be 0).
   * Used by the ADR-129 §6 localized swap to match a localized WS
   * frame's `alert_id` to its rendered history rows.
   */
  original_alert_id: number;
  /** Legacy display text `"<alert_id>: <alert name>"` — fallback alert link. */
  text: string | null;
}

export interface AlertHistoryListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: AlertHistory[];
}

export interface AlertHistoryFilters {
  schools?: string;
  type?: 'alert' | 'report';
  internal?: boolean;
  is_origin?: string;
  sort?: string;
  created_gte?: string;
  cancelled_lte?: string;
  page?: number;
  page_size?: number;
}

export async function getAlertHistory(
  filters: AlertHistoryFilters = {},
): Promise<AlertHistoryListResponse> {
  const result = await client.GET('/api/v2/alert-history-detailed/', {
    params: { query: filters as never },
  });
  return unwrap(result) as unknown as AlertHistoryListResponse;
}

export interface ActiveAlertListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: unknown[];
}

export interface ActiveAlertFilters {
  school?: number;
  page?: number;
  page_size?: number;
}

export async function getAlertHistoryDetail(
  id: number,
): Promise<AlertHistory> {
  // Use the LIST endpoint with `?ids=` filter rather than a singular detail
  // route — `/alert-history-detailed/{id}/` does not return data, but the
  // list view accepts `ids` and returns a paginated single-result page.
  const result = await client.GET('/api/v2/alert-history-detailed/', {
    params: { query: { ids: id } as never },
  });
  const list = unwrap(result) as unknown as AlertHistoryListResponse;
  const alertItem = list.results?.[0];
  if (!alertItem) {
    throw new Error(`Alert history ${id} not found`);
  }
  return alertItem;
}

// ============================================================================
// End an active alert / drill
// ============================================================================

export interface EndAlertPayload {
  cancel_reason?: string;
  cancel_description?: string;
}

export async function endAlert(
  id: number,
  payload: EndAlertPayload = {},
): Promise<void> {
  // DELETE /api/v2/alert/{id}/ with cancel reason/description in body —
  // mirrors AIM's `stopAlert`. Same endpoint serves alerts and drills.
  const result = await client.DELETE('/api/v2/alert/{id}/', {
    params: { path: { id } as never },
    body: payload as never,
  });
  unwrap(result);
}

// ============================================================================
// Alert History — Incident Detail (Events tab in the SMS event drawer)
// ============================================================================

export type IncidentEventType =
  | 'incident'
  | 'chat'
  | 'report'
  | 'notify'
  | 'map';

export interface IncidentDetailItem {
  id?: number;
  created_at: string;
  incident_type: string;
  author_name: string;
  content: string;
  has_attachments?: boolean;
  image_thumbnail?: string;
  video_thumbnail?: string;
}

export interface IncidentDetailResponse {
  history_data: {
    count: number;
    next: string | null;
    previous: string | null;
    results: IncidentDetailItem[];
  };
}

export interface IncidentDetailPayload {
  id: number;
  limit: number;
  offset: number;
  incident_types: string;
  query: string;
}

export async function getIncidentDetailExportJson(
  payload: IncidentDetailPayload,
): Promise<IncidentDetailResponse> {
  const result = await client.GET(
    '/api/v2/alert-history/{id}/incident-detail/export-json/',
    {
      params: {
        path: { id: payload.id } as never,
        query: {
          offset: payload.offset,
          limit: payload.limit,
          incident_types: payload.incident_types,
          query: payload.query,
        } as never,
      },
    },
  );
  return unwrap(result) as unknown as IncidentDetailResponse;
}

// ============================================================================
// Alert History — Delivery Report (Delivery tab in the SMS event drawer)
// ============================================================================

export interface DeliveryReportFunnel {
  push_attempted: number;
  push_sent: number;
  push_failed: number;
  sms_sent: number;
  sms_failed: number;
  email_sent: number;
  email_failed: number;
}

export interface AudienceFunnelStep {
  name: string;
  before: number;
  after: number;
}

export interface DeliveryReportData {
  funnel: DeliveryReportFunnel;
  by_type?: Partial<
    Record<'start' | 'end' | 'other', DeliveryReportFunnel | undefined>
  >;
  delivery_summary: {
    confirmed_fcm: number;
    confirmed_sms: number;
    confirmed_email: number;
    pending: number;
  };
  failure_reasons: Array<{
    channel: 'fcm' | 'sms' | 'email';
    failure_reason: string;
    count: number;
  }>;
  targeted_user_count?: number | null;
  no_token_count?: number | null;
  audience_detail_available?: boolean;
}

export interface AudienceDetailData {
  funnel: AudienceFunnelStep[];
}

export async function getDeliveryReport(
  alertId: number,
): Promise<DeliveryReportData> {
  const result = await client.GET(
    '/api/v2/alert-history/{id}/delivery-report/',
    {
      params: { path: { id: alertId } as never },
    },
  );
  return unwrap(result) as unknown as DeliveryReportData;
}

export async function getAudienceDetail(
  alertId: number,
): Promise<AudienceDetailData> {
  const result = await client.GET(
    '/api/v2/alert-history/{id}/audience-detail/',
    {
      params: { path: { id: alertId } as never },
    },
  );
  return unwrap(result) as unknown as AudienceDetailData;
}

// ============================================================================
// Alert History — Incident Detail Export (Download buttons in the Events tab)
// ============================================================================

export interface IncidentExportPayload {
  id: number;
  incident_types: string;
  query?: string;
}

type ExportFormat = 'pdf' | 'csv' | 'json';

const EXPORT_MIME: Record<ExportFormat, string> = {
  pdf: 'application/pdf',
  csv: 'text/csv',
  json: 'application/json',
};

async function downloadIncidentExport(
  payload: IncidentExportPayload,
  format: ExportFormat,
): Promise<boolean> {
  const params = new URLSearchParams();
  if (payload.incident_types) {
    params.append('incident_types', payload.incident_types);
  }
  if (payload.query) {
    params.append('query', payload.query);
  }
  const fallback = `alert_history_${payload.id}.${format}`;
  // downloadFile is the lib's blob-export helper; uses /api/v2 prefix already.
  const { blob, filename } = await client.downloadFile(
    `api/v2/alert-history/${payload.id}/incident-detail/export-${format}/?${params.toString()}`,
    { method: 'GET' },
    fallback,
  );
  if (blob.size === 0) return false;

  const typedBlob = new Blob([blob], { type: EXPORT_MIME[format] });
  const url = window.URL.createObjectURL(typedBlob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute(
    'download',
    filename.endsWith(`.${format}`) ? filename : `${filename}.${format}`,
  );
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
  return true;
}

export const getIncidentByFilterPDF = (payload: IncidentExportPayload) =>
  downloadIncidentExport(payload, 'pdf');

export const getIncidentByFilterCSV = (payload: IncidentExportPayload) =>
  downloadIncidentExport(payload, 'csv');

export const getIncidentByFilterJSON = (payload: IncidentExportPayload) =>
  downloadIncidentExport(payload, 'json');

// ============================================================================
// Active Alerts (currently in-progress only — used by Dashboard stat cards)
// ============================================================================

export async function getActiveAlerts(
  filters: ActiveAlertFilters = {},
): Promise<ActiveAlertListResponse> {
  const result = await client.GET('/api/v2/alert/', {
    params: { query: filters as never },
  });
  return unwrap(result) as unknown as ActiveAlertListResponse;
}
